import { useState, useCallback, useRef, useEffect, type KeyboardEvent } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { Button } from "@/components/lilia-ui/button";
import { BlockEditModal } from "../BlockEditModal";
import { IconPlus, IconTrash, IconIndentIncrease, IconIndentDecrease } from "@tabler/icons-react";
import type { AlgorithmLine } from "@/lib/lilia-stubs/types";
import styles from "../blocks/AlgorithmBlock.module.scss";

const KEYWORDS = [
  "", "if", "else", "else if", "while", "for", "foreach", "repeat", "until",
  "do", "end", "return", "break", "continue", "function", "procedure", "input", "output",
];

const KEYWORD_LABELS: Record<string, string> = {
  "": "(none)",
  "if": "if",
  "else": "else",
  "else if": "else if",
  "while": "while",
  "for": "for",
  "foreach": "foreach",
  "repeat": "repeat",
  "until": "until",
  "do": "do",
  "end": "end",
  "return": "return",
  "break": "break",
  "continue": "continue",
  "function": "function",
  "procedure": "procedure",
  "input": "input",
  "output": "output",
};

function createEmptyLine(): AlgorithmLine {
  return { indent: 0, keyword: "", text: "", comment: "" };
}

function parseLines(raw: unknown): AlgorithmLine[] {
  if (!Array.isArray(raw)) return [createEmptyLine()];
  return raw.map((item: unknown) => {
    if (typeof item === "object" && item !== null) {
      const obj = item as Record<string, unknown>;
      return {
        indent: typeof obj.indent === "number" ? obj.indent : 0,
        keyword: typeof obj.keyword === "string" ? obj.keyword : "",
        text: typeof obj.text === "string" ? obj.text : "",
        comment: typeof obj.comment === "string" ? obj.comment : "",
      };
    }
    return createEmptyLine();
  });
}

export function AlgorithmNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const caption = (node.attrs.caption as string) || "";
  const label = (node.attrs.label as string) || "";
  const rawLines = node.attrs.lines;
  const lines = parseLines(rawLines);

  const [isOpen, setIsOpen] = useState(false);
  const [draftCaption, setDraftCaption] = useState(caption);
  const [draftLabel, setDraftLabel] = useState(label);
  const [draftLines, setDraftLines] = useState<AlgorithmLine[]>(lines);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const textRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (focusIndex !== null && textRefs.current[focusIndex]) {
      textRefs.current[focusIndex]?.focus();
    }
  }, [focusIndex, draftLines.length]);

  const handleOpen = useCallback(() => {
    setDraftCaption(caption);
    setDraftLabel(label);
    setDraftLines(parseLines(rawLines));
    setIsOpen(true);
  }, [caption, label, rawLines]);

  const handleSave = useCallback(() => {
    updateAttributes({
      caption: draftCaption,
      label: draftLabel,
      lines: draftLines,
    });
    setIsOpen(false);
  }, [draftCaption, draftLabel, draftLines, updateAttributes]);

  const updateLine = useCallback((index: number, field: keyof AlgorithmLine, value: string | number) => {
    setDraftLines((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const addLine = useCallback((afterIndex?: number) => {
    setDraftLines((prev) => {
      const next = [...prev];
      const insertAt = afterIndex !== undefined ? afterIndex + 1 : next.length;
      const indent = afterIndex !== undefined ? next[afterIndex].indent : 0;
      next.splice(insertAt, 0, { indent, keyword: "", text: "", comment: "" });
      setFocusIndex(insertAt);
      return next;
    });
  }, []);

  const removeLine = useCallback((index: number) => {
    setDraftLines((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== index);
      return next;
    });
  }, []);

  const indentLine = useCallback((index: number, delta: number) => {
    setDraftLines((prev) => {
      const next = [...prev];
      const newIndent = Math.max(0, Math.min(6, next[index].indent + delta));
      next[index] = { ...next[index], indent: newIndent };
      return next;
    });
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addLine(index);
    } else if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      indentLine(index, 1);
    } else if (e.key === "Tab" && e.shiftKey) {
      e.preventDefault();
      indentLine(index, -1);
    } else if (e.key === "Backspace" && draftLines[index].text === "" && draftLines.length > 1) {
      e.preventDefault();
      removeLine(index);
      if (index > 0) setFocusIndex(index - 1);
    }
  }, [addLine, indentLine, removeLine, draftLines]);

  // Static preview
  const previewLines = lines.slice(0, 8);
  const hasMore = lines.length > 8;

  return (
    <NodeViewWrapper data-type="algorithm-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div className={styles.algorithmBlock} onClick={handleOpen} title="Click to edit algorithm">
        {caption && <div className={styles.caption}>{caption}</div>}
        <div className={styles.preview}>
          {previewLines.map((line, i) => (
            <div key={i} className={styles.previewLine} style={{ paddingLeft: line.indent * 20 }}>
              {line.keyword && <span className={styles.keyword}>{line.keyword} </span>}
              <span>{line.text}</span>
              {line.comment && <span className={styles.comment}>{" // "}{line.comment}</span>}
            </div>
          ))}
          {hasMore && <div className={styles.moreIndicator}>... {lines.length - 8} more lines</div>}
          {lines.length === 1 && !lines[0].text && (
            <span className={styles.emptyHint}>Click to add algorithm steps</span>
          )}
        </div>
      </div>

      <BlockEditModal
        sourceNode={node}
        title="Edit Algorithm"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={780}
      >
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 2 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Caption</label>
            <input
              type="text"
              value={draftCaption}
              onChange={(e) => setDraftCaption(e.target.value)}
              placeholder="e.g., Algorithm 1: Binary Search"
              style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Label</label>
            <input
              type="text"
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              placeholder="e.g., alg:bsearch"
              style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Lines</label>
        </div>

        <div className={styles.linesEditor}>
          {draftLines.map((line, index) => (
            <div key={index} className={styles.lineRow}>
              <div className={styles.lineControls}>
                <button
                  type="button"
                  className={styles.indentBtn}
                  onClick={() => indentLine(index, -1)}
                  disabled={line.indent <= 0}
                  title="Outdent"
                >
                  <IconIndentDecrease size={14} />
                </button>
                <button
                  type="button"
                  className={styles.indentBtn}
                  onClick={() => indentLine(index, 1)}
                  disabled={line.indent >= 6}
                  title="Indent"
                >
                  <IconIndentIncrease size={14} />
                </button>
              </div>
              <div className={styles.lineContent} style={{ paddingLeft: line.indent * 16 }}>
                <Select
                  value={line.keyword || "__none__"}
                  onValueChange={(val) => updateLine(index, "keyword", val === "__none__" ? "" : val)}
                >
                  <SelectTrigger className={styles.keywordSelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KEYWORDS.map((kw) => (
                      <SelectItem key={kw || "__none__"} value={kw || "__none__"}>
                        {KEYWORD_LABELS[kw]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input
                  ref={(el) => { textRefs.current[index] = el; }}
                  type="text"
                  value={line.text}
                  onChange={(e) => updateLine(index, "text", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onFocus={() => setFocusIndex(index)}
                  placeholder="Statement..."
                  className={styles.textInput}
                />
                <input
                  type="text"
                  value={line.comment || ""}
                  onChange={(e) => updateLine(index, "comment", e.target.value)}
                  placeholder="Comment"
                  className={styles.commentInput}
                />
              </div>
              <button
                type="button"
                className={styles.removeLineBtn}
                onClick={() => removeLine(index)}
                title="Remove line"
                disabled={draftLines.length <= 1}
              >
                <IconTrash size={14} />
              </button>
            </div>
          ))}
        </div>

        <Button
          variant="subtle"
          size="sm"
          onClick={() => addLine()}
          style={{ marginTop: 8 }}
        >
          <IconPlus size={16} /> Add line
        </Button>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
